import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { rowToItem, daysUntil, expiryColor, expiryLabel, formatQty } from "../lib/helpers.js";
import { CONTAINERS, CATEGORIES, CATEGORY_EMOJI, inferEmoji } from "../lib/constants.js";
import { track } from "../lib/analytics.js";
import AddItemModal from "../components/AddItemModal.jsx";
import BulkAddItemsModal from "../components/BulkAddItemsModal.jsx";
import ScanReceiptModal from "../components/ScanReceiptModal.jsx";
import ScanItemsModal   from "../components/ScanItemsModal.jsx";
import ItemDetailModal from "../components/ItemDetailModal.jsx";
import ManageInventoryModal from "../components/ManageInventoryModal.jsx";
import HouseholdShareModal from "../components/HouseholdShareModal.jsx";
import Layout from "../components/Layout.jsx";

// Round 3 — full CRUD. Click any row to open the detail modal (edit / use /
// mark-as-opened / delete). Click the "+ Add item" button to insert a new
// row. Mutations are optimistic where it's safe; errors revert via refetch.
export default function Fridge({ user }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [activeContainer, setActiveContainer] = useState("fridge");
  const [householdId, setHouseholdId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showScanReceipt, setShowScanReceipt] = useState(false);
  // v1.25 — Snap Items modal, parallel to ScanReceipt. Uses scan-items
  // Edge Function (5/day rate limit, vision prompt tuned for grocery photos).
  const [showScanItems,   setShowScanItems]   = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // v1.22 #257 — Web parity with iOS Manage inventory + invite flow.
  const [showManage, setShowManage] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  // householdName is loaded alongside householdId in refetch; the modal
  // needs it for its subtitle ("Greg's household · 2 members").
  const [householdName, setHouseholdName] = useState("");
  // v1.16 — search + category filter for the active container
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("All"); // "All" | "Dairy" | "Protein" | … | "expiring" | "expired"
  // v1.22 #235 (sky21__) — Fridge sort. Mirrors App.js. "added" default
  // matches existing behavior (newest first from created_at DESC query).
  const [sortBy, setSortBy] = useState("added");

  async function refetch() {
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) return;
      const { data: hhId } = await supabase.rpc("ensure_household_for_user");
      if (hhId) {
        setHouseholdId(hhId);
        // v1.22 #257 — Load household name for the Manage inventory modal
        // header. Mirrors iOS pattern (App.js ~line 9170). maybeSingle()
        // so a missing row resolves to null rather than throwing.
        const { data: hh } = await supabase
          .from("households").select("name").eq("id", hhId).maybeSingle();
        if (hh?.name) setHouseholdName(hh.name);
      }
      const { data, error } = await supabase
        .from("fridge_items")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setItems((data || []).map(rowToItem));
      setErr(null);
    } catch (e) {
      setErr(e?.message || "Couldn't load fridge.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refetch(); }, []);

  // v1.15 — expiring_soon_viewed mirrors the iOS event. Fires once per
  // load when items are present so we can correlate retention with
  // whether users actually hit the value moment.
  const expiringSeenRef = useRef(false);
  useEffect(() => {
    if (loading || expiringSeenRef.current) return;
    if (items.length === 0) return;
    const exp = items.filter(i => { const d = daysUntil(i.expiryDate); return d > 0 && d <= 3; }).length;
    const expd = items.filter(i => daysUntil(i.expiryDate) <= 0).length;
    track("expiring_soon_viewed", {
      total_items: items.length,
      expiring_count: exp,
      expired_count: expd,
      has_actionable: exp > 0 || expd > 0,
    });
    expiringSeenRef.current = true;
  }, [loading, items.length]);

  // v1.15 — debounced search_used (mirrors the iOS instrumentation).
  // Fires 600ms after the user stops typing if the query has >=2 chars.
  const lastSearchFiredRef = useRef("");
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2 || q === lastSearchFiredRef.current) return;
    const t = setTimeout(() => {
      track("search_used", { query_length: q.length, container: activeContainer });
      lastSearchFiredRef.current = q;
    }, 600);
    return () => clearTimeout(t);
  }, [searchQuery, activeContainer]);

  async function signOut() {
    await supabase.auth.signOut();
  }

  function handleAdded(row) {
    // New rows go to the top — same ordering as the fetched list (created_at desc)
    setItems(prev => [rowToItem(row), ...prev]);
  }

  function handleUpdated(row) {
    setItems(prev => prev.map(i => i.id === row.id ? rowToItem(row) : i));
    setSelectedItem(rowToItem(row));
  }

  function handleRemoved(id) {
    setItems(prev => prev.filter(i => i.id !== id));
    setSelectedItem(null);
  }

  const inContainer = items.filter(i => (i.container || "fridge") === activeContainer);

  // Stat tiles for the active container
  const containerCount = inContainer.length;
  const expiringSoon = inContainer.filter(i => {
    const d = daysUntil(i.expiryDate); return d > 0 && d <= 3;
  }).length;
  const expired = inContainer.filter(i => daysUntil(i.expiryDate) <= 0).length;

  // v1.16 — apply category filter then text search to the in-container set.
  const categoryFiltered = filter === "All"
    ? inContainer
    : filter === "expiring"
      ? inContainer.filter(i => { const d = daysUntil(i.expiryDate); return d > 0 && d <= 3; })
      : filter === "expired"
        ? inContainer.filter(i => daysUntil(i.expiryDate) <= 0)
        : inContainer.filter(i => i.category === filter);
  const searchTerm = searchQuery.trim().toLowerCase();
  const visible = searchTerm
    ? categoryFiltered.filter(i => (i.name || "").toLowerCase().includes(searchTerm))
    : categoryFiltered;
  // v1.22 #235 — apply user-selected sort. Items array is small enough
  // for an in-place slice+sort per render.
  const sorted = (() => {
    const arr = visible.slice();
    if (sortBy === "expiring") {
      arr.sort((a, b) => daysUntil(a.expiryDate) - daysUntil(b.expiryDate));
    } else if (sortBy === "longest") {
      arr.sort((a, b) => daysUntil(b.expiryDate) - daysUntil(a.expiryDate));
    } else if (sortBy === "az") {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return arr;
  })();

  return (
    <Layout user={user}>
      <>
        {/* v1.22 — header restructured for narrow viewports. sky21 reported
            the "Scan receipt" button overflowing screen edge and the
            "My Fridge" title squeezed on a Samsung S25 Ultra (~412px wide).
            Old layout was a single flex row with all 3 buttons + title,
            which gave each piece too little width on mobile. New layout
            stacks title row above the button row on <sm screens. */}
        <div className="mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text tracking-tight">My Fridge</h1>
            <p className="text-textSoft text-sm mt-0.5">
              {loading ? "Loading…" : `${items.length} items tracked across containers`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-start sm:justify-end mt-3 sm:mt-0 sm:-mt-12">
            {/* v1.22 #257 — Manage inventory entry point. Matches the iOS
                top-right pill: tinted-accent fill, accent border so it
                visually separates from the items-management buttons on
                its right (Add / Multi-add / Scan receipt are item-level
                actions; this one is household-level). */}
            <button
              onClick={() => setShowManage(true)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-accent/30 bg-accent/10 text-accent text-xs sm:text-sm font-semibold hover:bg-accent/20 whitespace-nowrap"
            >
              Manage inventory
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-border bg-card text-text text-xs sm:text-sm font-semibold hover:border-accent hover:text-accent whitespace-nowrap"
            >
              + Add
            </button>
            <button
              onClick={() => setShowBulkAdd(true)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-border bg-card text-text text-xs sm:text-sm font-semibold hover:border-accent hover:text-accent whitespace-nowrap"
            >
              + Multi-add
            </button>
            <button
              onClick={() => setShowScanReceipt(true)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-accent text-white text-xs sm:text-sm font-semibold hover:bg-accent/90 flex items-center gap-1.5 whitespace-nowrap"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Scan receipt
            </button>
            {/* v1.25 — Snap Items. Photo-of-groceries flow, sibling of
                Scan receipt. Accent-outline (not filled) so the two
                primary CTAs don't both fight for attention; receipt
                stays the visual default since it's the more common
                multi-add path. 🥬 emoji mirrors the iOS tile icon. */}
            <button
              onClick={() => setShowScanItems(true)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-accent text-accent text-xs sm:text-sm font-semibold hover:bg-accent/10 flex items-center gap-1.5 whitespace-nowrap"
            >
              <span aria-hidden="true">🥬</span>
              Snap items
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {CONTAINERS.map(c => (
            <button
              key={c.id}
              onClick={() => setActiveContainer(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
                activeContainer === c.id
                  ? "bg-accent text-white border-accent"
                  : "bg-card text-textSoft border-border hover:border-accent"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {!loading && containerCount > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-lg border border-border bg-card p-3 text-center">
              <p className="text-xl font-bold text-text">{containerCount}</p>
              <p className="text-[10px] text-textSoft uppercase tracking-wide">Total</p>
            </div>
            <button
              onClick={() => setFilter(filter === "expiring" ? "All" : "expiring")}
              className={`rounded-lg border p-3 text-center transition ${
                filter === "expiring" ? "border-warn bg-warn/5" : "border-border bg-card hover:border-warn/50"
              }`}
            >
              <p className="text-xl font-bold" style={{ color: expiringSoon > 0 ? "#EA580C" : "#1C261C" }}>{expiringSoon}</p>
              <p className="text-[10px] text-textSoft uppercase tracking-wide">Expiring soon</p>
            </button>
            <button
              onClick={() => setFilter(filter === "expired" ? "All" : "expired")}
              className={`rounded-lg border p-3 text-center transition ${
                filter === "expired" ? "border-danger bg-danger/5" : "border-border bg-card hover:border-danger/50"
              }`}
            >
              <p className="text-xl font-bold" style={{ color: expired > 0 ? "#DC2626" : "#1C261C" }}>{expired}</p>
              <p className="text-[10px] text-textSoft uppercase tracking-wide">Expired</p>
            </button>
          </div>
        )}

        {/* v1.16 — search bar + category filter chips. Mirrors iOS v1.0.10. */}
        {!loading && containerCount > 0 && (
          <>
            <div className="relative mb-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted text-sm">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${CONTAINERS.find(c => c.id === activeContainer)?.label.toLowerCase() || "items"}…`}
                className="w-full rounded-full border border-border bg-card pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-accent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-danger text-sm"
                  aria-label="Clear search"
                >✕</button>
              )}
            </div>
            <div className="flex gap-1.5 mb-4 flex-wrap">
              {["All", ...CATEGORIES].map(c => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                    filter === c
                      ? "bg-accent/10 border-accent text-accent"
                      : "bg-card border-border text-textSoft hover:border-accent"
                  }`}
                >
                  {c === "All" ? "All" : `${CATEGORY_EMOJI[c]} ${c}`}
                </button>
              ))}
            </div>
          </>
        )}

        {err && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger mb-4">
            {err}
          </div>
        )}

        {!loading && visible.length === 0 && !err && (
          <div className="rounded-xl border border-border bg-card p-10 text-center">
            <div className="text-5xl mb-3">{searchQuery || filter !== "All" ? "🔍" : "🧊"}</div>
            <p className="text-text font-semibold">
              {searchQuery
                ? `No items match "${searchQuery}"`
                : filter !== "All"
                  ? `Nothing matches the ${filter} filter`
                  : items.length === 0
                    ? "Your fridge is empty"
                    : `Nothing in ${CONTAINERS.find(c => c.id === activeContainer)?.label}`}
            </p>
            <p className="text-textSoft text-sm mt-1 mb-4">
              {searchQuery || filter !== "All"
                ? "Try clearing the filter."
                : "Snap a grocery receipt to fill it in 5 seconds."}
            </p>
            {searchQuery || filter !== "All" ? (
              <button
                onClick={() => { setSearchQuery(""); setFilter("All"); }}
                className="px-5 py-2 rounded-full border border-border text-sm font-semibold text-textSoft hover:bg-card"
              >
                Clear filters
              </button>
            ) : (
              <div className="flex gap-2 justify-center flex-wrap">
                <button
                  onClick={() => setShowScanReceipt(true)}
                  className="px-5 py-2 rounded-full bg-accent text-white text-sm font-semibold hover:bg-accent/90"
                >
                  Scan a receipt
                </button>
                <button
                  onClick={() => setShowAdd(true)}
                  className="px-5 py-2 rounded-full border border-border text-sm font-semibold text-textSoft hover:bg-card"
                >
                  + Add item
                </button>
              </div>
            )}
          </div>
        )}

        {/* v1.22 #235 — Sort chip. Tap cycles through the 4 sort modes.
            Hidden when there's 1 or fewer items (nothing to sort). */}
        {visible.length > 1 && (
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={() => {
                const order = ["added", "expiring", "longest", "az"];
                const next = order[(order.indexOf(sortBy) + 1) % order.length];
                setSortBy(next);
                try { track("fridge_sort_changed", { from: sortBy, to: next }); } catch {}
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-bg border border-border text-text text-xs font-semibold hover:border-accent/40 transition"
              aria-label={`Sort: ${sortBy}. Tap to change.`}
            >
              <span className="text-textSoft">↕</span>
              <span>{sortBy === "added" ? "Recently added" : sortBy === "expiring" ? "Expires soonest" : sortBy === "longest" ? "Expires latest" : "A→Z"}</span>
            </button>
          </div>
        )}

        <div className="space-y-2">
          {sorted.map(it => {
            const days = daysUntil(it.expiryDate);
            const color = expiryColor(days);
            return (
              <button
                key={it.id}
                onClick={() => setSelectedItem(it)}
                className="w-full rounded-xl border border-border bg-card p-4 flex items-center gap-3 text-left hover:border-accent/60 transition"
              >
                <div className="w-10 h-10 rounded-lg bg-bg flex items-center justify-center text-xl flex-shrink-0">
                  {inferEmoji(it.name, it.emoji || CATEGORY_EMOJI[it.category] || "📦")}
                </div>
                <div className="flex-1 min-w-0">
                  {/* v1.22 #237 (sky21__) — let brand-prefixed names
                      use 2 lines so receipt-scan duplicates with the
                      same prefix become distinguishable. Tailwind
                      line-clamp-2 enforces the cap. */}
                  <p className="text-text font-semibold line-clamp-2">{it.name}</p>
                  <p className="text-textSoft text-xs mt-0.5">
                    {formatQty(it)} · {it.category}
                    {it.isOpened && <span className="ml-1 text-warn">· opened</span>}
                  </p>
                </div>
                <div className="text-xs font-semibold whitespace-nowrap" style={{ color }}>
                  {expiryLabel(days)}
                </div>
              </button>
            );
          })}
        </div>

        <AddItemModal
          open={showAdd}
          onClose={() => setShowAdd(false)}
          onAdded={handleAdded}
          householdId={householdId}
          defaultContainer={activeContainer}
        />

        <BulkAddItemsModal
          open={showBulkAdd}
          onClose={() => setShowBulkAdd(false)}
          onAdded={handleAdded}
          householdId={householdId}
          defaultContainer={activeContainer}
        />

        <ScanReceiptModal
          open={showScanReceipt}
          onClose={() => setShowScanReceipt(false)}
          onAdded={handleAdded}
          householdId={householdId}
          defaultContainer={activeContainer}
        />

        {/* v1.25 — Snap Items modal. Same props shape as ScanReceiptModal so
            the onAdded refresh / householdId / defaultContainer flow stays
            uniform across the two parallel scan flows. */}
        <ScanItemsModal
          open={showScanItems}
          onClose={() => setShowScanItems(false)}
          onAdded={handleAdded}
          householdId={householdId}
          defaultContainer={activeContainer}
        />

        <ItemDetailModal
          open={!!selectedItem}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdated={handleUpdated}
          onRemoved={handleRemoved}
          onLeftoverSaved={handleAdded}
        />

        {/* v1.22 #257 — Manage inventory + share-household flow.
            Manage inventory is the iOS-parity landing page (containers,
            members, invite CTA). The "Invite a family member" button
            inside it closes itself and pops the share modal (existing
            HouseholdShareModal which handles both create + redeem). */}
        <ManageInventoryModal
          open={showManage}
          onClose={() => setShowManage(false)}
          items={items}
          householdName={householdName}
          onOpenInvite={() => setShowInvite(true)}
        />

        <HouseholdShareModal
          open={showInvite}
          onClose={() => setShowInvite(false)}
          onJoined={() => { setShowInvite(false); refetch(); }}
        />
      </>
    </Layout>
  );
}
